import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Users, Clock, MapPin, Plus, Video } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDate, formatTime, formatScheduleLabel, lessonFormForHeadcount } from '@/lib/utils';
import { statusLabel, courseTypeForHeadcount } from '@/lib/deLabels';
import DateInput from '@/components/ui/date-input';
import { useActionBarConfig } from '@/contexts/ActionBarContext';
import { writeSelectedRecord } from '@/lib/selectedRecord';
import EmailLink from '@/components/EmailLink';

export default function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isSuperAdmin } = useTenant();
  const roleNames = new Set((user?.roles || []).map((role) => role.name));
  const isTeacher = (roleNames.has('teacher') || roleNames.has('lehrer'))
    && !isSuperAdmin
    && !['admin', 'secretary', 'sekretariat', 'verwaltung', 'management']
      .some((role) => roleNames.has(role));
  const [course, setCourse] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingLessons, setGeneratingLessons] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);

  const [generateForm, setGenerateForm] = useState({
    start_date: '',
    end_date: '',
    frequency: 'weekly'
  });

  useEffect(() => {
    if (id !== 'new') {
      loadCourse();
      loadEnrollments();
      loadLessons();
    } else {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!course?.id) return;
    writeSelectedRecord({
      courseId: course.id,
      teacherId: course.teacher_id || null,
      locationId: course.location_id || null,
      dayOfWeek: course.day_of_week ?? null,
      rhythm: course.rhythm || null,
      startTime: course.start_time || null,
    });
  }, [course]);

  const loadCourse = async () => {
    try {
      const response = await api.get(`/courses/${id}`);
      setCourse(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden des Kurses');
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };

  const loadEnrollments = async () => {
    try {
      const response = await api.get(`/courses/${id}/enrollments`);
      setEnrollments(response.data || []);
    } catch (error) {
      console.error('Error loading enrollments:', error);
    }
  };

  const loadLessons = async () => {
    try {
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
      const response = await api.get('/lessons/calendar', {
        params: {
          start_date: today.toISOString().split('T')[0],
          end_date: nextMonth.toISOString().split('T')[0],
          course_id: id
        }
      });
      setLessons(response.data || []);
    } catch (error) {
      console.error('Error loading lessons:', error);
    }
  };

  const participantIds = enrollments
    .filter((e) => e.status === 'active')
    .map((e) => e.student_id || e.student?.id)
    .filter(Boolean);

  useActionBarConfig({
    list: { kind: 'student', ids: participantIds },
  }, [participantIds.join(',')]);

  const handleGenerateLessons = async () => {
    if (!generateForm.start_date || !generateForm.end_date) {
      toast.error('Bitte Start- und Enddatum eingeben');
      return;
    }

    setGeneratingLessons(true);
    try {
      const response = await api.post('/lessons/generate', {
        course_id: parseInt(id),
        start_date: generateForm.start_date,
        end_date: generateForm.end_date,
        frequency: generateForm.frequency
      });
      toast.success(response.data.message);
      setGenerateDialogOpen(false);
      loadLessons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Generieren der Termine');
    } finally {
      setGeneratingLessons(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: { label: 'Aktiv', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      waitlist: { label: 'Warteliste', className: 'bg-amber-50 text-amber-700 border-amber-200' },
      paused: { label: 'Pausiert', className: 'bg-slate-100 text-slate-700' },
      ended: { label: 'Beendet', className: 'bg-slate-100 text-slate-700' },
    };
    const config = variants[status] || variants.active;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getCourseTypeBadge = (type) => {
    const labels = {
      individual: 'Einzelunterricht',
      group: 'Gruppenunterricht',
      workshop: 'Workshop',
    };
    const activeCount = enrollments.filter((e) => e.status === 'active').length;
    return courseTypeForHeadcount(type, activeCount) || labels[type] || type;
  };

  const dayNames = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-red"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="course-name">
                {lessonFormForHeadcount(
                  formatScheduleLabel(course?.name) || course?.name,
                  enrollments.filter((e) => e.status === 'active').length,
                )}
              </h1>
              <div className="flex items-center gap-3">
                {getStatusBadge(course?.status)}
                <Badge variant="outline">{getCourseTypeBadge(course?.course_type)}</Badge>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(`/unterrichtsverwaltung?course_id=${id}`)}
                data-testid="open-in-uv-button"
                className="border-slate-300 bg-white text-slate-800 hover:border-brand-red hover:text-brand-red"
              >
                In Unterrichtsverwaltung öffnen
              </Button>
              <Button
                onClick={() => navigate(`/online/${id}`)}
                data-testid="start-online-lesson-button"
                className="bg-brand-red text-white hover:bg-brand-red/90"
              >
                <Video className="w-4 h-4 mr-2" />
                Onlineunterricht starten
              </Button>
              {!isTeacher && (
                <Button
                  onClick={() => navigate(`/courses/${id}/edit`)}
                  data-testid="edit-course-button"
                  className="bg-slate-900 text-white hover:bg-slate-800"
                >
                  Bearbeiten
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-slate-500">Zeitplan</p>
                <p className="text-sm text-slate-900">
                  {course?.day_of_week !== null ? dayNames[course.day_of_week] : '-'}
                  {course?.start_time && `, ${formatTime(course.start_time)} Uhr`}
                </p>
                <p className="text-xs text-slate-500">{course?.duration_minutes} Minuten</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-slate-500">Kapazität</p>
                <p className="text-sm text-slate-900">
                  {enrollments.filter(e => e.status === 'active').length} / {course?.max_capacity}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-slate-500">Raum</p>
                <p className="text-sm text-slate-900">{course?.room_name || 'Kein Raum zugewiesen'}</p>
              </div>
            </div>
          </div>
        </div>

        <Tabs defaultValue="participants" className="w-full">
          <TabsList className="border-b border-slate-200 w-full justify-start rounded-none bg-transparent p-0">
            <TabsTrigger value="participants" data-testid="tab-participants">Teilnehmer</TabsTrigger>
            <TabsTrigger value="schedule" data-testid="tab-schedule">Termine</TabsTrigger>
          </TabsList>

          <TabsContent value="participants" className="mt-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-slate-900">Teilnehmer</h2>
                {!isTeacher && (
                <Button
                  onClick={() => toast.info('Funktion wird noch implementiert')}
                  data-testid="add-participant-button"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Teilnehmer hinzufügen
                </Button>
                )}
              </div>
              {enrollments.length > 0 ? (
                <div className="space-y-3">
                  {enrollments.map((enrollment) => (
                    <div key={enrollment.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <button
                          type="button"
                          className="font-medium text-slate-900 hover:text-brand-red hover:underline text-left"
                          data-testid={`course-open-enrollment-${enrollment.id}`}
                          onClick={() => navigate(`/unterrichtsverwaltung?enrollment_id=${enrollment.id}`)}
                        >
                          {enrollment.student
                            ? `${enrollment.student.first_name} ${enrollment.student.last_name}`
                            : `Schüler ID: ${enrollment.student_id}`}
                        </button>
                        {enrollment.student?.email && (
                          <p className="text-sm text-slate-500"><EmailLink email={enrollment.student.email} /></p>
                        )}
                        <p className="text-sm text-slate-600">Seit: {formatDate(enrollment.created_at)}</p>
                      </div>
                      {getStatusBadge(enrollment.status)}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Keine Teilnehmer</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="schedule" className="mt-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-slate-900">Termine</h2>
                {!isTeacher && (
                <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="generate-lessons-button" size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      Termine generieren
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Termine automatisch generieren</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="start_date">Startdatum</Label>
                        <DateInput
                          id="start_date"
                          value={generateForm.start_date}
                          onChange={(e) => setGenerateForm({ ...generateForm, start_date: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="end_date">Enddatum</Label>
                        <DateInput
                          id="end_date"
                          value={generateForm.end_date}
                          onChange={(e) => setGenerateForm({ ...generateForm, end_date: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="frequency">Rhythmus</Label>
                        <Select
                          value={generateForm.frequency}
                          onValueChange={(value) => setGenerateForm({ ...generateForm, frequency: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Wöchentlich</SelectItem>
                            <SelectItem value="biweekly">14-tägig</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={handleGenerateLessons}
                        disabled={generatingLessons}
                        className="w-full"
                      >
                        {generatingLessons ? 'Generiere...' : 'Termine erstellen'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                )}
              </div>
              {lessons.length > 0 ? (
                <div className="space-y-3">
                  {lessons.map((lesson) => (
                    <div
                      key={lesson.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                      onClick={() => navigate(`/lessons/${lesson.id}`)}
                      data-testid={`lesson-${lesson.id}`}
                    >
                      <div>
                        <p className="font-medium text-slate-900">
                          {formatDate(lesson.date)} - {formatTime(lesson.start_time)} Uhr
                        </p>
                        <p className="text-sm text-slate-600">
                          {lesson.teacher_name || 'Kein Lehrer'} • {lesson.room_name || 'Kein Raum'}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-slate-600">{lesson.student_count} Teilnehmer</span>
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          {statusLabel(lesson.status)}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  {isTeacher
                    ? 'Keine Termine vorhanden.'
                    : 'Keine Termine vorhanden. Generieren Sie Termine für diesen Kurs.'}
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}